import { useEffect } from 'react';
import { omit } from 'lodash-es';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { useQueryClient } from '@tanstack/react-query';
import { appConfig } from '../utils/consts.ts';
import { convertObjectKeysOrArrayValuesToCamelCase } from '../utils/helpers.ts';
import { GameRoundStatus, HorseName } from '../utils/racing-game/consts.ts';
import { StableName } from '../utils/racing-game/horses';
import { CacheData } from '../@types/cacheData';
import { useGameResultStore } from '../services/store/racing-game';


export const useGameWebsocketsSubscription = () => {
  const queryClient = useQueryClient();
  const setGameResults = useGameResultStore(({ setData }) => setData);

  const { readyState, lastMessage } = useWebSocket(appConfig.wsAPI, {
    shouldReconnect: () => true,
    reconnectInterval: 2000,
    reconnectAttempts: Infinity,
    onOpen: () => {
      console.log('WebSocket connected');
    },
    onError: (event) => {
      console.error('WebSocket error', event);
    },
    onClose: (event) => {
      console.log('WebSocket disconnected', event.code, event.reason);
    },
  });

  useEffect(() => {
    if (!lastMessage) return;

    try {
      const data = JSON.parse(lastMessage.data);

      if (import.meta.env.DEV) {
        // console.log('websocket data', data);
      }

      switch (data.type) {
        case 'roundData':
          queryClient.setQueryData<CacheData['gameRoundData']>(
            ['gameRoundData'],
            convertObjectKeysOrArrayValuesToCamelCase(
              omit(data as WSMessages['roundData'], 'type')
            )
          );
          break;
        case 'race_results':
          if (import.meta.env.DEV) {
            console.log('🎯 RACE RESULTS RECEIVED:', {
              round: data.round,
              top3: data.results?.top3,
              winnerGroup: data.results?.winnerGroup,
            });
          }
          setGameResults(omit(data as WSMessages['race_results'], 'type'));
          break;
        default:
      }
    } catch (error) {
      console.error('Error parsing WebSocket message', error);
    }
  }, [lastMessage, queryClient, setGameResults]);

  useEffect(() => {
    const connectionStatus = {
      [ReadyState.CONNECTING]: 'Connecting',
      [ReadyState.OPEN]: 'Open',
      [ReadyState.CLOSING]: 'Closing',
      [ReadyState.CLOSED]: 'Closed',
      [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
    }[readyState];

    console.log('WebSocket connection status:', connectionStatus);
  }, [readyState]);
};

/*{
  "type": "roundData",
  "round": 1044,
  "status": 1,
  "position": {
  "doge": 8333.3333,
    "shib": 8287.2333,
    "pepe": 8332.5333,
    "bonk": 8193.9333,
    "wif": 7772.7333,
    "floki": 8164.8333,
    "brett": 8215.9333,
    "popcat": 7446.2333
},
  "growth": {
  "doge": -0.1416,
    "shib": -0.1877,
    "pepe": -0.1424,
    "bonk": -0.281,
    "wif": -0.7022,
    "floki": -0.3101,
    "brett": -0.259,
    "popcat": -1.0287
},
  "startPrices": {
  "doge": 0.17842259,
    "shib": 0.000013638200000000001,
    "pepe": 0.0000088472,
    "bonk": 0.000020281400000000002,
    "wif": 0.64170659,
    "floki": 0.00007857920000000001,
    "brett": 0.06589212,
    "popcat": 0.41508503
},
  "currentPrices": {
  "doge": {
    "price": 0.17817,
      "previousPrice": 0.17823529,
      "percentageChange": -0.14156839669236781
  },
  "shib": {
    "price": 0.0000136126,
      "previousPrice": 0.000013615900000000001,
      "percentageChange": -0.18770805531521922
  },
  "pepe": {
    "price": 0.0000088346,
      "previousPrice": 0.0000088347,
      "percentageChange": -0.14241794013925899
  },
  "bonk": {
    "price": 0.000020224400000000002,
      "previousPrice": 0.0000202277,
      "percentageChange": -0.28104568718135636
  },
  "wif": {
    "price": 0.63720063,
      "previousPrice": 0.63762579,
      "percentageChange": -0.7021838438654526
  },
  "floki": {
    "price": 0.00007833550000000001,
      "previousPrice": 0.0000783578,
      "percentageChange": -0.31013296139436264
  },
  "brett": {
    "price": 0.06572143,
      "previousPrice": 0.06576015,
      "percentageChange": -0.2590446323475419
  },
  "popcat": {
    "price": 0.41081509,
      "previousPrice": 0.41109068,
      "percentageChange": -1.0286904348248904
  }
},
  "time_remaining": 4,
  "mul": {
  "a": {},
  "sumA": 0,
    "b": {},
  "sumB": 0
}
}*/

/*{
  "type": "race_results",
  "results": {
  "top3": {
    "first": {
      "token": "wif",
        "growth": "0.17"
    },
    "second": {
      "token": "popcat",
        "growth": "0.12"
    },
    "third": {
      "token": "shib",
        "growth": "0.07"
    }
  }
},
  "round": 1046,
  "mul": {
  "a": {},
  "sumA": 0,
    "b": {},
  "sumB": 0
}
}*/

type PriceDetails = {
  price: number;
  previousPrice: number;
  percentageChange: number;
};

type Muls = {
  [key in '1' | '2' | '99']: {
    a: Record<string, unknown>;
    sumA: number;
    b: Record<string, unknown>;
    sumB: number;
  };
};

export type RoundDataResponse = {
  type: 'roundData';
  round: number;
  status: GameRoundStatus;
  start_at: number;
  end_at: number;
  time_remaining: number;
  position: Record<HorseName, number>;
  growth: Record<HorseName, number>;
  startPrices: Record<HorseName, number>;
  currentPrices: Record<HorseName, PriceDetails>;
  mul: Muls;
};

export type RaceResultsResponse = {
  type: 'race_results';
  results: {
    top3: {
      first: {
        token: HorseName;
        growth: string;
      };
      second: {
        token: HorseName;
        growth: string;
      };
      third: {
        token: HorseName;
        growth: string;
      };
    };
    winnerGroup?: StableName;
  };
  round: number;
  mul: Muls;
};

type WSMessages = {
  roundData: RoundDataResponse;
  race_results: RaceResultsResponse;
};
